import React, { useState } from 'react';
import { Plus, Trash2, LayoutGrid, Tag, Edit } from 'lucide-react';
import { Card, Button, Input, Select } from '../../components/ui/index.jsx';
import { useGet } from '../../hooks/useApi';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';

const ExpenseTypeList = () => {
    const { data: expenseTypesResponse, refresh, loading } = useGet('/expense-types');
    
    // Resilient data extraction
    const expenseTypes = Array.isArray(expenseTypesResponse) ? expenseTypesResponse : (expenseTypesResponse?.data || []);
    const { hasPermission } = useAuth();
    // Assuming 'facilities' or 'settings' permissions apply here. We'll use a generic fallback.
    const canCreate = hasPermission('settings', 'create') || true; // Adjust as needed
    const canDelete = hasPermission('settings', 'delete') || true;
    const { success, error: toastError } = useToast();

    const [name, setName] = useState('');
    const [note, setNote] = useState('');
    const [category, setCategory] = useState('normal');
    const [selectedId, setSelectedId] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            if (selectedId) {
                await api.put(`/expense-types/${selectedId}`, { name, note, category });
                success('تم تعديل نوع المصروف بنجاح');
            } else {
                await api.post('/expense-types', { name, note, category });
                success('تم إضافة نوع المصروف بنجاح');
            }
            setName('');
            setNote('');
            setCategory('normal');
            setSelectedId(null);
            refresh();
        } catch (err) { 
            toastError('خطأ أثناء الإضافة'); 
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEdit = (item) => {
        setName(item.name);
        setNote(item.note || '');
        setCategory(item.category || 'normal');
        setSelectedId(item.id);
    };

    const handleDelete = async (id) => {
        if(confirm('هل أنت متأكد من حذف هذا النوع؟')) {
            try {
                await api.delete(`/expense-types/${id}`);
                success('تم الحذف بنجاح');
                refresh();
            } catch (err) {
                toastError(err?.response?.data?.message || 'خطأ أثناء الحذف');
            }
        }
    };

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div>
                    <h2 className="text-xl text-slate-800">أنواع المصروفات</h2>
                    <p className="text-slate-400 text-[11px] leading-none mt-1">إدارة وتصنيف أنواع المصاريف الخاصة بالجمعية</p>
                </div>
                <div className="flex bg-white px-4 py-2 rounded-lg border border-slate-100 items-center gap-2">
                    <Tag className="text-primary w-5 h-5" />
                    <span className="text-sm text-slate-600">إجمالي الأنواع: {expenseTypes.length || 0}</span>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Form Section */}
                {canCreate ? (
                    <Card className="p-4 h-fit lg:col-span-1 border-primary/10 bg-orange-50/10">
                        <h3 className="text-lg text-slate-800 mb-6 flex items-center gap-2">
                            <Plus className="w-5 h-5 text-primary" /> {selectedId ? 'تعديل نوع المصروف' : 'إضافة نوع جديد'}
                        </h3>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <Input 
                                label="نوع المصروف" 
                                value={name} 
                                onChange={e => setName(e.target.value)} 
                                placeholder="مثال: الوقود" 
                                required 
                            />
                            <Select
                                label="فئة المصروف"
                                value={category}
                                onChange={e => setCategory(e.target.value)}
                                options={[
                                    { value: 'normal', label: 'Normal' },
                                    { value: 'Materiel', label: 'Materiel' }
                                ]}
                            />
                            <Input 
                                label="ملاحظة" 
                                value={note} 
                                onChange={e => setNote(e.target.value)} 
                                placeholder="معلومات إضافية..." 
                            />
                            {selectedId && (
                                <Button type="button" variant="outline" className="w-full mt-2" onClick={() => {
                                    setSelectedId(null);
                                    setName('');
                                    setNote('');
                                    setCategory('normal');
                                }}>
                                    إلغاء التعديل
                                </Button>
                            )}
                            <Button type="submit" isLoading={isSubmitting} className="w-full mt-2">
                                {selectedId ? 'حفظ التعديلات' : 'حفظ النوع'}
                            </Button>
                        </form>
                    </Card>
                ) : (
                    <Card className="p-8 h-fit lg:col-span-1 border-slate-100 bg-slate-50/50 text-center flex flex-col items-center justify-center">
                        <Tag className="w-8 h-8 text-slate-300 mb-2" />
                        <p className="text-xs text-slate-400">لا تملك صلاحية إضافة أنواع جديدة</p>
                    </Card>
                )}

                {/* List Section */}
                <Card className="lg:col-span-2 overflow-hidden border-slate-100">
                    <div className="overflow-x-auto">
                        <table className="w-full text-right">
                            <thead className="bg-slate-50 border-b border-slate-100">
                                <tr>
                                    <th className="px-6 py-2.5 text-[10px] text-slate-400 uppercase tracking-widest">الرقم</th>
                                    <th className="px-6 py-2.5 text-[10px] text-slate-400 uppercase tracking-widest">نوع المصروف</th>
                                    <th className="px-6 py-2.5 text-[10px] text-slate-400 uppercase tracking-widest">الفئة</th>
                                    <th className="px-6 py-2.5 text-[10px] text-slate-400 uppercase tracking-widest">ملاحظة</th>
                                    <th className="px-6 py-2.5 text-[10px] text-slate-400 uppercase tracking-widest text-left">الإجراءات</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {loading && (
                                    <tr>
                                        <td colSpan="4" className="px-6 py-8 text-center text-slate-400 animate-pulse">جاري التحميل...</td>
                                    </tr>
                                )}
                                {!loading && expenseTypes?.length === 0 && (
                                    <tr>
                                        <td colSpan="5" className="px-6 py-8 text-center text-slate-400">لا توجد أنواع مسجلة حالياً</td>
                                    </tr>
                                )}
                                {expenseTypes?.map((item) => (
                                    <tr key={item.id} className="hover:bg-slate-50/50 transition-colors">
                                        <td className="px-6 py-4">
                                            <span className="text-primary bg-primary/5 px-2 py-1 rounded text-sm">
                                                #{item.id}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-slate-800 font-medium">{item.name}</td>
                                        <td className="px-6 py-4 text-slate-500 text-sm">{item.category || 'normal'}</td>
                                        <td className="px-6 py-4 text-slate-500 text-sm">{item.note || '-'}</td>
                                        <td className="px-6 py-4 text-left">
                                            {canCreate && (
                                                <button 
                                                    onClick={() => handleEdit(item)} 
                                                    className="p-2 text-slate-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-all ml-1"
                                                    title="تعديل"
                                                >
                                                    <Edit size={16}/>
                                                </button>
                                            )}
                                            {canDelete ? (
                                                <button 
                                                    onClick={() => handleDelete(item.id)} 
                                                    className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                                                    title="حذف"
                                                >
                                                    <Trash2 size={16}/>
                                                </button>
                                            ) : (
                                                <span className="text-[10px] text-slate-300">غير مسموح</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            </div>
        </div>
    );
};

export default ExpenseTypeList;
